import api from './API';

/**
 * User Service
 * Handles all user-related API calls
 */

export const userService = {
  /**
   * Get all users
   * @param {Object} params - Query parameters (page, limit, search, role, isActive)
   * @returns {Promise} API response with users list
   */
  getUsers: (params = {}) => api.get('/users', { params }),

  /**
   * Get single user by ID
   * @param {string} id - User ID
   * @returns {Promise} API response with user details
   */
  getUser: (id) => api.get(`/users/${id}`),

  /**
   * Create new user
   * @param {Object} data - User data
   * @returns {Promise} API response with created user
   */
  createUser: (data) => api.post('/users', data),

  /**
   * Update user
   * @param {string} id - User ID
   * @param {Object} data - Updated user data
   * @returns {Promise} API response with updated user
   */
  updateUser: (id, data) => api.put(`/users/${id}`, data),

  /**
   * Delete user
   * @param {string} id - User ID
   * @returns {Promise} API response
   */
  deleteUser: (id) => api.delete(`/users/${id}`),

  /**
   * Get users by role
   * @param {string} role - User role (admin, inventory_manager, etc.)
   * @param {Object} params - Additional query parameters
   * @returns {Promise} API response with filtered users
   */
  getUsersByRole: (role, params = {}) => 
    api.get('/users', { params: { ...params, role } }),

  /**
   * Get current user profile
   * @returns {Promise} API response with current user data
   */
  getCurrentUser: () => api.get('/users/me'),

  /**
   * Update current user profile
   * @param {Object} data - Updated profile data
   * @returns {Promise} API response with updated user
   */
  updateProfile: (data) => api.put('/users/me', data),

  /**
   * Change password
   * @param {Object} data - Password change data (currentPassword, newPassword)
   * @returns {Promise} API response
   */
  changePassword: (data) => api.put('/users/me/password', data),

  /**
   * Get user activity log
   * @param {string} id - User ID
   * @param {Object} params - Query parameters
   * @returns {Promise} API response with activity log
   */
  getUserActivity: (id, params = {}) => 
    api.get(`/users/${id}/activity`, { params }),

  /**
   * Activate user
   * @param {string} id - User ID
   * @returns {Promise} API response
   */
  activateUser: (id) => api.put(`/users/${id}/activate`),

  /**
   * Deactivate user
   * @param {string} id - User ID
   * @returns {Promise} API response
   */
  deactivateUser: (id) => api.put(`/users/${id}/deactivate`),

  /**
   * Reset user password (admin only)
   * @param {string} id - User ID
   * @param {Object} data - New password data
   * @returns {Promise} API response
   */
  resetUserPassword: (id, data) => api.put(`/users/${id}/reset-password`, data),

  /**
   * Get users with manager/admin roles
   * @param {Object} params - Query parameters
   * @returns {Promise} API response with manager users
   */
  getManagerUsers: (params = {}) => {
    return api.get('/users', { 
      params: { 
        ...params, 
        limit: params.limit || 100,
        isActive: true 
      } 
    }).then(response => {
      // Filter for manager roles on client side if backend doesn't support role filtering
      const users = response.data?.data?.users || response.data?.users || [];
      const managerUsers = users.filter(user => 
        ['admin', 'inventory_manager', 'department_manager'].includes(user.role)
      );
      return {
        ...response,
        data: {
          ...response.data,
          users: managerUsers,
          data: {
            ...response.data?.data,
            users: managerUsers
          }
        }
      };
    });
  },

  /**
   * Search users
   * @param {string} query - Search query
   * @param {Object} params - Additional parameters
   * @returns {Promise} API response with search results
   */
  searchUsers: (query, params = {}) => 
    api.get('/users/search', { params: { q: query, ...params } }),
};

export default userService;
